import type { WidgetInstance } from "@kourai/api/home-board";
import {
  WIDGET_SIZES,
  type WidgetBrowser,
  type WidgetDefinition,
  type WidgetPermission,
  type WidgetSize,
  type WidgetSurface,
} from "./widget";

// biome-ignore lint/suspicious/noExplicitAny: a registry holds widgets of every config type
type AnyWidget = WidgetDefinition<any>;

/**
 * The widgets a board can show, by type (ADMIN-257). Pure: no React, no browser, so it's easy to
 * test and to ship in the widgets SDK.
 */
export type WidgetRegistry = {
  get: (type: string) => AnyWidget | undefined;
  /** Widgets people can add on this surface: not pinned or app made ones, in registration order. */
  addable: (surface: WidgetSurface) => AnyWidget[];
  all: () => AnyWidget[];
};

export function createRegistry(widgets: readonly AnyWidget[]): WidgetRegistry {
  const byType = new Map<string, AnyWidget>();
  for (const w of widgets) {
    if (byType.has(w.type)) throw new Error(`widget type "${w.type}" is registered twice`);
    byType.set(w.type, w);
  }
  return {
    get: (type) => byType.get(type),
    addable: (surface) =>
      widgets.filter((w) => !w.pinned && w.addable !== false && w.surfaces.includes(surface)),
    all: () => [...widgets],
  };
}

export type ResolvedConfig<Config> =
  | { ok: true; config: Config; migrated: boolean }
  | { ok: false; error: string };

/**
 * An instance's config, ready for the component: migrated from older versions, parsed with
 * defaults filled in. A config from a newer version (saved by a newer app on another device) isn't
 * guessed at: the tile says the widget needs an update instead of losing settings.
 */
export function resolveConfig<Config>(
  def: WidgetDefinition<Config>,
  instance: Pick<WidgetInstance, "config" | "typeVersion">,
): ResolvedConfig<Config> {
  if (instance.typeVersion > def.version)
    return {
      ok: false,
      error: `Saved by a newer version of ${def.title}. Update Kourai to see it.`,
    };
  let raw = instance.config;
  const migrated = instance.typeVersion < def.version;
  if (migrated && def.migrate) raw = def.migrate(structuredClone(raw), instance.typeVersion);
  const parsed = def.config.safeParse(raw);
  if (!parsed.success) return { ok: false, error: `${def.title} settings aren't valid.` };
  return { ok: true, config: parsed.data, migrated };
}

/**
 * Checks a config patch from `host.setConfig` against the widget's schema. Returns the new
 * config, or null (and the board keeps the old one) when the patch doesn't fit.
 */
export function patchConfig<Config>(
  def: WidgetDefinition<Config>,
  current: Config,
  patch: Partial<Config>,
): Config | null {
  const parsed = def.config.safeParse({ ...current, ...patch });
  return parsed.success ? parsed.data : null;
}

/**
 * `host.browser` for one widget: only the APIs it declared. `api` is the extension's `browser`
 * object (or a fake in tests). Undeclared APIs are absent, not stubbed, so a widget that forgot
 * to declare one fails right away in development. Each key is read from `api` when used, so an
 * optional permission granted later shows up without rebuilding the host.
 */
export function gateBrowser(
  permissions: readonly WidgetPermission[],
  api: Partial<Record<WidgetPermission, unknown>>,
): WidgetBrowser {
  const gated: WidgetBrowser = {};
  for (const p of permissions)
    Object.defineProperty(gated, p, { enumerable: true, get: () => api[p] });
  return Object.freeze(gated);
}

/**
 * The cells for a size, clamped to what the widget allows (its default, else its first size).
 *
 * @internal exported for tests
 */
export function cellsFor(def: AnyWidget, size?: WidgetSize) {
  const pick = size && def.sizes.includes(size) ? size : (def.defaultSize ?? def.sizes[0] ?? "m");
  return { size: pick, ...WIDGET_SIZES[pick] };
}

/** A new instance of a widget, unplaced (the grid finds it a spot). */
export function newInstance(def: AnyWidget, id: string, size?: WidgetSize): WidgetInstance {
  const { w, h } = cellsFor(def, size);
  const parsed = def.config.safeParse({});
  return {
    id,
    type: def.type,
    typeVersion: def.version,
    x: 0,
    y: 0,
    w,
    h,
    config: (parsed.success ? parsed.data : {}) as Record<string, unknown>,
  };
}
